/**
 * MoneyInput — atlas Input with an in-box "$" prefix and the standard
 * money mask (thousands separators while typing, canonical raw value
 * out). Pairs `moneyDigits` + `formatMoneyMask` the same way the
 * Schedule's editable cells do.
 *
 * Controlled: `value` is the canonical raw string ("1234.56"); the
 * caller decides when to react (typically `onBlur` → commit).
 *
 * Keeps a synchronous local buffer (mirrors `EditableTextCell`) so the
 * typed character paints instantly even when the parent's re-render is
 * deferred (see `useDeferredSetter`). Re-syncs only on a genuine external
 * change — never on the lagging echo of our own emit — so a fast typist
 * never sees a character revert.
 */
import { useEffect, useRef, useState } from "react";
import { Input } from "@tricura/atlas-ui";
import { formatMoneyMask, moneyDigits } from "../utils/money";

interface MoneyInputProps {
    /** Canonical raw value, e.g. "1234.56". */
    value: string;
    onChange: (raw: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    isDisabled?: boolean;
    isInvalid?: boolean;
    size?: "sm" | "md" | "lg";
    "aria-label"?: string;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

export function MoneyInput({
    value,
    onChange,
    onBlur,
    placeholder = "00,00",
    isDisabled,
    isInvalid,
    size = "sm",
    "aria-label": ariaLabel,
    "data-autotest": dataAutotest,
}: MoneyInputProps) {
    const lastEmitted = useRef(value);
    const [local, setLocal] = useState(value);
    useEffect(() => {
        if (value !== lastEmitted.current) {
            lastEmitted.current = value;
            setLocal(value);
        }
    }, [value]);

    const handleChange = (raw: string) => {
        const next = moneyDigits(raw);
        lastEmitted.current = next;
        setLocal(next);
        onChange(next);
    };

    return (
        <div className="relative w-full">
            <span aria-hidden="true" className="text-tertiary pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm">
                $
            </span>
            <Input
                aria-label={ariaLabel}
                data-autotest={dataAutotest}
                size={size}
                placeholder={placeholder}
                value={formatMoneyMask(local)}
                onChange={handleChange}
                onBlur={onBlur}
                isDisabled={isDisabled}
                isInvalid={isInvalid}
                inputMode="decimal"
                autoComplete="off"
                // `pr-3` re-pins the right padding: atlas widens it to `pr-9`
                // whenever it thinks a trailing icon is present (invalid state),
                // which would shove the right-aligned digits sideways the moment
                // the field errors.
                inputClassName="pl-7 pr-3 text-right"
                // Atlas's Input auto-renders a trailing InfoCircle on isInvalid
                // (input.tsx's `hasTrailingIcon`) and it lands on top of the
                // right-aligned money text. Its own base classes are
                // `hidden group-invalid/input:block`, so a plain `hidden` here
                // loses to the variant — override the variant itself. Invalid
                // state reads from the ring + hint text alone, same as the
                // Submission Title field (account-basics.tsx).
                tooltipClassName="hidden group-invalid/input:hidden"
            />
        </div>
    );
}
